import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  Collapse, 
  IconButton,
  Box,
  Divider
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

/**
 * FormSection component using Material-UI
 * This component provides a consistent wrapper for grouping form fields
 * with collapsible functionality and proper ARIA attributes for accessibility.
 */
const FormSection = ({
  title,
  description,
  children,
  collapsible = false,
  defaultExpanded = true,
  className = '',
  variant = 'default', // 'default' | 'card' | 'subtle'
  spacing = 'md', // 'sm' | 'md' | 'lg'
  ...props
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  // Generate unique IDs for accessibility
  const sectionId = React.useMemo(() => 
    `section-${Math.random().toString(36).substring(2, 9)}`, []
  );
  const contentId = `${sectionId}-content`;
  const headerId = `${sectionId}-header`;

  const toggleExpanded = () => {
    if (collapsible) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleKeyDown = (event) => {
    if (collapsible && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      toggleExpanded();
    }
  };

  // Map spacing to MUI spacing values
  const getSpacing = () => {
    switch (spacing) {
      case 'sm':
        return { xs: 2, sm: 2 };
      case 'lg':
        return { xs: 3, sm: 4 };
      case 'md':
      default:
        return { xs: 2, sm: 3 };
    }
  };

  // Get variant styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'card':
        return {
          component: Paper,
          elevation: 1,
          sx: { p: getSpacing(), mb: 3 }
        };
      case 'subtle':
        return {
          component: Box,
          sx: { 
            p: getSpacing(), 
            mb: 3,
            backgroundColor: 'background.subtle',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }
        };
      case 'default':
      default:
        return {
          component: Box,
          sx: { mb: 3 }
        };
    }
  };

  const variantProps = getVariantStyles();
  const Component = variantProps.component;

  return (
    <Component
      className={className}
      {...variantProps}
      {...props}
      data-testid="form-section"
    >
      {(title || description) && (
        <Box
          id={headerId}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: collapsible ? 'pointer' : 'default',
            mb: variant === 'default' ? 2 : 1,
            '&:hover': collapsible ? {
              backgroundColor: 'action.hover',
              borderRadius: 1,
              mx: -1,
              px: 1,
              py: 0.5,
            } : {}
          }}
          onClick={collapsible ? toggleExpanded : undefined}
          onKeyDown={collapsible ? handleKeyDown : undefined}
          tabIndex={collapsible ? 0 : undefined}
          role={collapsible ? 'button' : undefined}
          aria-expanded={collapsible ? isExpanded : undefined}
          aria-controls={collapsible ? contentId : undefined}
          data-testid="form-section-header"
        >
          <Box sx={{ flex: 1 }}>
            {title && (
              <Typography 
                variant="h6" 
                component="h3"
                sx={{ 
                  fontWeight: 600,
                  mb: description ? 0.5 : 0,
                  color: 'text.primary'
                }}
                data-testid="form-section-title"
              >
                {title}
              </Typography>
            )}
            
            {description && (
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ mb: 0 }}
                data-testid="form-section-description"
              >
                {description}
              </Typography>
            )}
          </Box>

          {collapsible && (
            <IconButton
              size="small"
              aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
              sx={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme => theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shorter,
                }),
                ml: 1
              }}
              data-testid="form-section-toggle"
            >
              <ExpandMore />
            </IconButton>
          )}
        </Box>
      )}

      {variant === 'default' && (title || description) && (
        <Divider sx={{ mb: 2 }} />
      )}
      
      <Collapse 
        in={!collapsible || isExpanded} 
        timeout="auto"
        unmountOnExit={false}
      >
        <Box
          id={contentId}
          aria-labelledby={title ? headerId : undefined}
          aria-hidden={collapsible && !isExpanded}
          sx={{
            '& > *:last-child': {
              mb: 0
            }
          }}
          data-testid="form-section-content"
        >
          {children}
        </Box>
      </Collapse>
    </Component>
  );
};

export default FormSection;